import { atom } from 'jotai';
import { SWRResponse } from 'swr';
import {
  getOmnitagResultJobStatusKey,
  sentItemKeysAtom,
} from 'packages/omnitag/state/job-status';
import { OmnitagSearchResp } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { OmnitagFilter } from 'packages/lib/api/omnitag/contract/omnitag-filters';
import { atomWithReset } from 'jotai/utils';

// Aug-11-2025 - Used Gemini 2.5 Agent to make filters dynamic (DynamicFilters, StaticSearchQuery, defaultSearchQuery)
// Aug-20-2025 - Used Gemini 2.5 Agent to add support for 'number' filters
// Sep-11-2025 - Used Gemini 2.5 Agent to revamp filter types to use Zod OmnitagFilter type instead of relying on OMNITAG_FILTERS_CONFIG static data
// Oct-20-2025 - Used Gemini 2.5 Agent to assist with migration from Recoil to Jotai
// May-20-2026 - Used Cursor 'Auto' to move pagination client-side (created filteredSearchResultsTotalCountAtom and paginatedFilteredResultsSelector)
// Jun-10-2026 - Used Cursor 'Auto' to cache filtered results separately from pagination (sentItemKeysAtom)

type SelectFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'select' }
>['paramName'];
type TextFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'text' }
>['paramName'];
type NumberFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'number' }
>['paramName'];
type ToggleFilterParamName = Extract<
  OmnitagFilter,
  { filterType: 'toggle' }
>['paramName'];

export type SelectFilters = { [K in SelectFilterParamName]: string[] };
export type TextFilters = { [K in TextFilterParamName]: string };
export type NumberFilters = { [K in NumberFilterParamName]: number | null };
export type ToggleFilters = { [K in ToggleFilterParamName]: boolean };

/** These fiters are hard-coded, rather than defined in backend filters config */
export interface StaticSearchQuery {
  text: string;
  logNames: string[];
  pageNo?: number;
  pageSize?: number;
}

// Combine them into the final SearchQuery type
export type SearchQuery = StaticSearchQuery &
  SelectFilters &
  TextFilters &
  NumberFilters &
  ToggleFilters;

export const searchQueryAtom = atomWithReset<SearchQuery | null>(null);

export const hideSentItemsAtom = atom(false);

export const searchPageNoAtom = atomWithReset(1);
export const searchPageSizeAtom = atomWithReset(48);

/** Full search results from the API (not paginated client-side) */
export const allSearchResultsAtom = atomWithReset<
  SWRResponse<OmnitagSearchResp> | undefined
>(undefined);

/** Filtered search results (hide sent items). Recomputes when search results, hide-sent toggle, or known sent keys change — not on pagination. */
export const filteredSearchResultsListAtom = atom((get) => {
  const searchResults = get(allSearchResultsAtom);

  if (!searchResults || searchResults.isValidating || !searchResults.data) {
    return [];
  }

  const hideSentItems = get(hideSentItemsAtom);
  const allResults = searchResults.data.imageMetadata ?? [];

  if (!hideSentItems) {
    return allResults;
  }

  const sentItemKeys = get(sentItemKeysAtom);
  return allResults.filter(
    (result) =>
      !sentItemKeys.has(
        getOmnitagResultJobStatusKey(result.log_name, result.timestamp)
      )
  );
});

/** Total count after client-side filters (e.g. hide sent items), before pagination */
export const filteredSearchResultsTotalCountAtom = atom((get) => {
  return get(filteredSearchResultsListAtom).length;
});

/** @returns an SWRResponse for the current page of filtered search results */
export const paginatedFilteredResultsSelector = atom((get) => {
  const searchResults = get(allSearchResultsAtom);
  const filteredList = get(filteredSearchResultsListAtom);

  if (!searchResults || searchResults.isValidating || !searchResults.data) {
    return searchResults;
  }

  const pageNo = get(searchPageNoAtom);
  const pageSize = get(searchPageSizeAtom);
  const startIndex = (pageNo - 1) * pageSize;
  const paginatedResults = filteredList.slice(
    startIndex,
    startIndex + pageSize
  );

  return { ...searchResults, data: paginatedResults };
});

export const requestIDAtom = atom<string | null>(null);
